import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Fade, Grid, Stack, Typography } from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';
import Svg from 'react-inlinesvg';
import { useAtom, useSetAtom } from 'jotai';
import { GenericModal } from '@/components/shared/GenericModal';
import { abyssColors } from '@/theme/colors';
import {
  ActionButton,
  FooterText,
  ImageFrame,
  LineBreak,
  PlaceholderText,
  SectionTitle,
  SelectedOverlay,
  SelectionDialogActions,
  SelectionDialogContent,
  Subtitle,
  UploadCTA,
  crossIconStyles,
  headerStyles,
  modalStyles,
  paperStyles,
} from './styles';
import { CheckIcon } from '@/assets/icons/CheckIcon';
import { MappedReportPointOfInterests } from '@/types';
import {
  File,
  FileTypes,
  ReportDetailsDocument,
  ReportDetailsQuery,
  useUpdatePoiImagesMutation,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { ImageIcon } from '@/assets/icons/ImageIcon';
import { dialogOverlay } from '@/components/UserManagement/styles';
import { Image } from '@/components/Analysis/shared/Image';

type ReportDocument = ReportDetailsQuery['report'];

type ModalProps = {
  open: boolean;
  handleClose: () => void;
  disableUpload?: boolean;
  poiDetails: MappedReportPointOfInterests;
  allImagesOfSelectedPOI: File[];
  allPdfsOfSelectedPOI?: File[];
};

const showError = (message: string) => {
  return (
    <div>
      <Alert severity="error">{message}</Alert>
    </div>
  );
};

const showSuccess = (message: string) => {
  return <Alert severity="success">{message}</Alert>;
};

const UploadSection = () => {
  return (
    <>
      <Stack gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <SectionTitle>Upload from your device</SectionTitle>
          <Subtitle>(max 4 uploads)</Subtitle>
        </Stack>
        <UploadCTA>
          <Stack alignItems="center">
            <ArrowUpward sx={{ fontSize: '1.6rem', color: abyssColors.primary[300] }} />
            <Subtitle>Upload</Subtitle>
          </Stack>
        </UploadCTA>
      </Stack>
      <LineBreak />
    </>
  );
};

export const ImageSelectionModal = ({
  open,
  handleClose,
  disableUpload = false,
  poiDetails,
  allImagesOfSelectedPOI,
  allPdfsOfSelectedPOI,
}: ModalProps) => {
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);
  const [selectedFiles, setSelectedFiles] = useState<(File | null)[]>(
    selectedReport?.selectedPointOfInterestImages?.find(
      (selected) => selected?.pointOfInterestId === poiDetails.id
    )?.selectedImages ?? []
  );
  const initializeSelectedImages = useCallback(() => {
    if (selectedReport) {
      const poiSelectedImages = selectedReport.selectedPointOfInterestImages?.find(
        (selected) => selected?.pointOfInterestId === poiDetails.id
      )?.selectedImages;

      const poiSelectedPdfs = selectedReport.allPointOfInterestPdfs
        ?.find((pdf) => pdf?.pointOfInterestId === poiDetails.id)
        ?.selectedPdfs?.filter((pdf) => pdf?.file && pdf.selectedStatus)
        ?.map((p) => p.file);

      if (poiSelectedImages) {
        setSelectedFiles([...poiSelectedImages, ...(poiSelectedPdfs || [])]);
      }
    }
  }, [poiDetails.id, selectedReport]);

  useEffect(() => {
    initializeSelectedImages();
  }, [initializeSelectedImages, poiDetails.id, selectedReport]);

  useEffect(() => {
    if (open) {
      initializeSelectedImages();
    } else {
      setSelectedFiles([]);
    }
  }, [initializeSelectedImages, open]);

  const [mutateSelectedImages, { loading }] = useUpdatePoiImagesMutation({
    refetchQueries: [ReportDetailsDocument],
    onCompleted: (response) => {
      if (!response?.updateReportPointOfInterestImages) {
        setSnackbarMessage({
          shouldShow: true,
          content: showError('Something went wrong'),
        });
        return;
      }
      setSnackbarMessage({
        shouldShow: true,
        content: showSuccess('Selected files updated successfully'),
      });
      setSelectedReport(
        (previous) =>
          ({
            ...previous,
            selectedPointOfInterestImages:
              response?.updateReportPointOfInterestImages.selectedPointOfInterestImages,
          } as ReportDocument)
      );
    },
    onError: (errorReponse) => {
      setSnackbarMessage({
        shouldShow: true,
        content: showError(errorReponse.message),
      });
    },
  });

  const handleSelection = (image: File) => {
    const index = selectedFiles.findIndex(
      (selected) => selected?.resourcePath === image.resourcePath
    );
    if (index === -1) {
      setSelectedFiles([...selectedFiles, image]);
    } else {
      setSelectedFiles(
        selectedFiles.filter((selected) => selected?.resourcePath !== image.resourcePath)
      );
    }
  };

  const handleUpdate = () => {
    const mappedImages = selectedFiles
      .filter((selected) => selected?.type !== FileTypes.Pdf)
      .map((selected) => {
        return selected
          ? {
              name: selected.name,
              fileName: selected.fileName,
              type: selected.type,
              resourcePath: selected.resourcePath,
              uuid: selected?.uuid ?? undefined,
              imageType: selected?.imageType ?? undefined,
            }
          : selected;
      });

    const mappedPdfs = selectedFiles
      .filter((selected) => selected && selected?.type === FileTypes.Pdf)
      .map((selected) => {
        return selected
          ? {
              name: selected.name,
              fileName: selected.fileName,
              type: selected.type,
              resourcePath: selected.resourcePath,
            }
          : selected;
      });

    if (selectedReport?.id) {
      mutateSelectedImages({
        variables: {
          input: {
            reportId: selectedReport?.id,
            pointOfInterestId: poiDetails.id,
            selectedImages: mappedImages,
            selectedPdfs: mappedPdfs,
          },
        },
      })
        .then((response) => {
          const updatedPdfList =
            response?.data?.updateReportPointOfInterestImages.allPointOfInterestPdfs;
          if (updatedPdfList)
            setSelectedReport((previous) => {
              if (!previous) return previous;
              return {
                ...previous,
                allPointOfInterestPdfs: updatedPdfList,
              };
            });
        })
        .finally(() => {
          handleClose();
        });
    }
  };

  const selectedResourcePaths = useMemo(
    () => new Set(selectedFiles.map((file) => file?.resourcePath).filter(Boolean)),
    [selectedFiles]
  );

  const MediaGridItem = ({
    file,
    renderContent,
  }: {
    file: File;
    renderContent: (file: File) => React.ReactNode;
  }) => {
    const isSelected = selectedResourcePaths.has(file.resourcePath);

    return (
      <Grid item xs={4} key={file.fileName}>
        <Stack direction="column" alignItems="center" gap={0.5}>
          <ImageFrame onClick={() => handleSelection(file)} data-testid="selection-image">
            <Fade data-testid={isSelected ? 'selected-image' : 'unselected-image'} in={isSelected}>
              <SelectedOverlay>
                <CheckIcon />
              </SelectedOverlay>
            </Fade>
            {renderContent(file)}
          </ImageFrame>
          <Typography
            fontSize={'0.9rem'}
            textAlign="center"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {file.fileName}
          </Typography>
        </Stack>
      </Grid>
    );
  };

  const renderMediaGrid = (files: File[], renderContent: (file: File) => React.ReactNode) => {
    return (
      <Grid container sx={{ width: '408px' }} spacing={2}>
        {files.map((file) => (
          <MediaGridItem key={file.fileName} file={file} renderContent={renderContent} />
        ))}
      </Grid>
    );
  };

  const renderEmptyState = () => (
    <Stack height={disableUpload ? '332px' : '194px'} justifyContent="center">
      <Stack alignItems="center" gap={1}>
        <ImageIcon />
        <PlaceholderText>No files available for selection</PlaceholderText>
      </Stack>
    </Stack>
  );

  const renderLoader = () => (
    <Stack height={disableUpload ? '332px' : '194px'} justifyContent="center">
      <Stack alignItems="center" gap={1}>
        <PlaceholderText>Loading...</PlaceholderText>
      </Stack>
    </Stack>
  );

  return (
    <GenericModal
      open={open}
      handleClose={handleClose}
      title="Adding more files"
      sx={{ ...modalStyles, ...dialogOverlay }}
      backgroundPaperStyle={paperStyles}
      headerStyleProp={headerStyles}
      crossIconStyle={crossIconStyles}
    >
      <SelectionDialogContent data-testid="image-selection">
        {disableUpload || <UploadSection />}
        <Stack gap={2}>
          <SectionTitle>Select from uploaded files</SectionTitle>

          {loading
            ? renderLoader()
            : (() => {
                const allFiles = [...allImagesOfSelectedPOI, ...(allPdfsOfSelectedPOI || [])];

                if (allFiles.length === 0) {
                  return renderEmptyState();
                }

                return renderMediaGrid(allFiles, (file) => {
                  const isPdf = file.type === FileTypes.Pdf;

                  return isPdf ? (
                    <Stack justifyContent="center" alignItems="center" height={'100%'}>
                      <Svg
                        style={{
                          width: '50%',
                          height: '50%',
                        }}
                        src="/assets/pdf_logo.svg"
                      />
                    </Stack>
                  ) : (
                    <Image
                      imgSx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      sx={{ position: 'initial' }}
                      src={getCloudfrontUrl(file?.resourcePath ?? '')}
                      alt={file.name}
                      fallbackSize="2.4rem"
                      fontSize="1rem"
                    />
                  );
                });
              })()}
        </Stack>
      </SelectionDialogContent>
      <SelectionDialogActions>
        <FooterText>{selectedFiles.length} selected</FooterText>
        <Stack direction="row" gap={2}>
          <ActionButton onClick={handleClose}>
            <span>Cancel</span>
          </ActionButton>
          <ActionButton
            variant="contained"
            contained
            onClick={handleUpdate}
            disabled={selectedFiles.length === 0}
          >
            Add
          </ActionButton>
        </Stack>
      </SelectionDialogActions>
    </GenericModal>
  );
};
